'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarRange } from 'lucide-react';
import { cn } from '@/lib/cn';
import { regionFormat } from '@/lib/region-format';
import { CONTROL_CLASS } from './input';
import { Segmented } from './segmented';
import { TimezoneSelect } from './timezone-select';
import { useAnchoredPanel } from './use-anchored-panel';
import { MonthCalendar } from './date-time-picker';

export interface DateRange {
  from: Date;
  to: Date;
}

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function endOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
}

function daysAgo(n: number): Date {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() - n);
  return d;
}

function startOfWeek(d: Date): Date {
  const c = startOfDay(d);
  const lead = (c.getDay() + 6) % 7; // Monday-first
  c.setDate(c.getDate() - lead);
  return c;
}

// Pay periods run the 1st to the 15th and the 16th to month end.
export function payPeriodContaining(d: Date): DateRange {
  const firstHalf = d.getDate() <= 15;
  const from = new Date(d.getFullYear(), d.getMonth(), firstHalf ? 1 : 16);
  const to = firstHalf ? new Date(d.getFullYear(), d.getMonth(), 15) : new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from, to: endOfDay(to) };
}

const PRESETS: { label: string; range: () => DateRange }[] = [
  { label: 'Today', range: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { label: 'Yesterday', range: () => ({ from: daysAgo(1), to: endOfDay(daysAgo(1)) }) },
  { label: 'This week', range: () => ({ from: startOfWeek(new Date()), to: endOfDay(new Date()) }) },
  { label: 'This pay period', range: () => payPeriodContaining(new Date()) },
  {
    label: 'Last pay period',
    range: () => {
      const previous = payPeriodContaining(new Date()).from;
      previous.setDate(previous.getDate() - 1);
      return payPeriodContaining(previous);
    },
  },
  {
    label: 'Last week',
    range: () => {
      const thisWeek = startOfWeek(new Date());
      const from = new Date(thisWeek);
      from.setDate(from.getDate() - 7);
      const to = new Date(thisWeek);
      to.setDate(to.getDate() - 1);
      return { from, to: endOfDay(to) };
    },
  },
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOfDay(now) };
    },
  },
  {
    label: 'Last month',
    range: () => {
      const now = new Date();
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        to: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
  {
    label: 'Last 3 months',
    range: () => {
      const now = new Date();
      return { from: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()), to: endOfDay(now) };
    },
  },
];

function fmtDay(d: Date): string {
  return regionFormat.formatDate(d, { month: 'short', day: 'numeric' });
}

function sameRange(a: DateRange, b: DateRange): boolean {
  return (
    startOfDay(a.from).getTime() === startOfDay(b.from).getTime() &&
    startOfDay(a.to).getTime() === startOfDay(b.to).getTime()
  );
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** IANA zone the range is interpreted in; renders the timezone select when set. */
  timezone?: string;
  onTimezoneChange?: (timezone: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * The standard range toolbar: [range] [presets] [timezone]. Sits directly
 * under the page title on every ranged page. Clicking the range opens a
 * from/to calendar.
 */
export function DateRangePicker({
  value,
  onChange,
  timezone,
  onTimezoneChange,
  disabled,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  // The preset the user last clicked. Some days two presets resolve to the
  // same range — on a Monday "This week" (week-to-date) equals "Today", and on
  // the 1st of a month "This month" does too — so we can't reliably recover the
  // active preset from the range alone. Remembering the click keeps the right
  // segment highlighted instead of snapping to whichever preset is listed first.
  const [activePreset, setActivePreset] = useState<string | null>(null);
  // Custom selection: first click picks `from`, second picks `to`.
  const [pendingFrom, setPendingFrom] = useState<Date | null>(null);
  const [hoverDay, setHoverDay] = useState<Date | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const panelStyle = useAnchoredPanel(open, popRef, panelRef, close, { estimatedHeight: 330 });

  useEffect(() => {
    if (open) {
      setPendingFrom(null);
      setHoverDay(null);
    }
  }, [open]);

  const pickDay = (day: Date) => {
    if (!pendingFrom) {
      setPendingFrom(startOfDay(day));
      return;
    }
    const from = pendingFrom <= day ? pendingFrom : startOfDay(day);
    const to = pendingFrom <= day ? endOfDay(day) : endOfDay(pendingFrom);
    onChange({ from, to });
    setActivePreset(null); // hand-picked range is "Custom", not a preset
    setPendingFrom(null);
    setOpen(false);
  };

  // Prefer the remembered preset while its range still matches the current
  // value; otherwise derive from the range (falling back to "custom"). This
  // guarantees exactly one active segment even when presets overlap.
  const activeKey =
    activePreset != null && PRESETS.some((p) => p.label === activePreset && sameRange(value, p.range()))
      ? activePreset
      : (PRESETS.find((p) => sameRange(value, p.range()))?.label ?? 'custom');

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <div ref={popRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={cn(CONTROL_CLASS, 'flex w-auto items-center gap-2 text-left')}
        >
          <CalendarRange className="size-3.5 shrink-0 text-zinc-400" aria-hidden />
          <span className="whitespace-nowrap tabular-nums">
            {fmtDay(value.from)} – {fmtDay(value.to)}
          </span>
        </button>
        {open && panelStyle
          ? createPortal(
          <div ref={panelRef} style={panelStyle} className="corner-brand-sm border border-zinc-700 bg-zinc-900 p-3 shadow-xl shadow-black/40">
            <MonthCalendar
              selected={null}
              onPick={pickDay}
              onHoverDay={setHoverDay}
              rangeStart={pendingFrom ?? value.from}
              rangeEnd={pendingFrom ? hoverDay : value.to}
            />
          </div>,
          document.body)
          : null}
      </div>
      <Segmented
        disabled={disabled}
        segments={[...PRESETS.map((p) => ({ key: p.label, label: p.label })), { key: 'custom', label: 'Custom' }]}
        value={activeKey}
        onChange={(key) => {
          const preset = PRESETS.find((p) => p.label === key);
          if (preset) {
            setActivePreset(key);
            onChange(preset.range());
          } else {
            setActivePreset(null);
            setOpen(true);
          }
        }}
      />
      {timezone && onTimezoneChange ? (
        <TimezoneSelect value={timezone} onChange={onTimezoneChange} disabled={disabled} />
      ) : null}
    </div>
  );
}
